import { useMemo, useState, useEffect } from "react";
import { useZoomDetection } from "../useZoomDetection";

interface UseModalHeightOptions {
  smallScreenOffset?: number;
  largeScreenOffset?: number;
  baseClassName?: string;
  responsive?: boolean;
  zoomThreshold?: number;
}

export function useModalHeight(options: UseModalHeightOptions = {}) {
  const {
    smallScreenOffset = 40,
    largeScreenOffset = 100,
    baseClassName = "relative bg-bg-brand-secondary-default rounded-xl w-[100%] md:w-[480px] overflow-y-auto",
    responsive = true,
    zoomThreshold = 2
  } = options;

  const [windowHeight, setWindowHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 800
  );

  const isZoomedIn = useZoomDetection(zoomThreshold);

  useEffect(() => {
    if (!responsive || typeof window === 'undefined') return;

    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [responsive]);

  const modalStyle = useMemo(() => {
    // Return empty object if zoomed in
    if (isZoomedIn) {
      return {};
    }
    
    return {
      height: windowHeight < 800
        ? `${windowHeight - smallScreenOffset}px`
        : `${windowHeight - largeScreenOffset}px`,
    };
  }, [windowHeight, smallScreenOffset, largeScreenOffset, isZoomedIn]);

  const modalClassName = useMemo(() => baseClassName, [baseClassName]);

  return {
    modalStyle,
    modalClassName,
    isZoomedIn
  };
} 